import { apiClient } from '../client';
import { 
  ActionTransformUI, 
  ActionTransformValues, 
  ActionTransformValuesDTO,
  ValidationResult, 
  ComponentChangeEvent, 
  DropdownOption 
} from '../types';
import { createContextLogger } from '../../utils/logger';

const logger = createContextLogger('TransformsService');
import { telemetryService } from '../index';

export class TransformsService {
  /**
   * Get UI configuration for a specific transform type
   */
  async getTransformUIConfiguration(transformType: string): Promise<ActionTransformUI> {
    try {
      logger.debug('Fetching transform UI configuration for:', transformType);
      logger.debug('🔗 API Call: GET /v1/pipeline/transform-ui/' + transformType);
      
      const config = await apiClient.get<ActionTransformUI>(`/v1/pipeline/transform-ui/${transformType}`);
      
      logger.debug('Transform UI configuration fetched successfully:', {
        transformType,
        pagesCount: config.pages?.length || 0,
        transformName: config.name
      });

      logger.debug('✅ UI config API response:', {
        transformType,
        transformName: config.name,
        pagesCount: config.pages?.length || 0,
        pages: config.pages?.map(p => ({ id: p.id, title: p.title, componentCount: p.components.length }))
      });

      return config;
    } catch (error) {
      logger.error('Failed to fetch transform UI configuration:', error);
      telemetryService.trackError(error as Error, 'transform-ui-fetch', {
        endpoint: `/v1/pipeline/transform-ui/${transformType}`,
        transformType,
        errorType: error instanceof Error ? error.constructor.name : 'UnknownError',
        errorMessage: error instanceof Error ? error.message : String(error)
      });
      throw error;
    }
  }

  /**
   * Get current values for a specific transform instance
   */
  async getTransformValues(path: string, transformName: string, transformType: string): Promise<ActionTransformValues> {
    try {
      logger.debug('Fetching transform values:', { path, transformName, transformType });
      const values = await apiClient.get<ActionTransformValues>(
        `/v1/pipeline/transform-values`,
        { params: { path, transformName, transformType } }
      );
      
      logger.debug('Transform values fetched successfully:', {
        transformName,
        transformType,
        valuesCount: Object.keys(values.values || {}).length
      });

      return values;
    } catch (error) {
      logger.error('Failed to fetch transform values:', error);
      throw error;
    }
  }

  /**
   * Save transform configuration values
   */
  async saveTransformValues(
    path: string, 
    transformId: string, 
    values: ActionTransformValues
  ): Promise<ValidationResult> {
    try {
      logger.debug('Saving transform values:', { path, transformId, values });
      const result = await apiClient.post<ValidationResult>(
        `/v1/pipeline/transform-values/${transformId}`,
        values,
        { params: { path } }
      );
      
      logger.debug('Transform values saved successfully:', {
        transformId,
        isValid: result.valid
      });

      return result;
    } catch (error) {
      logger.error('Failed to save transform values:', error);
      throw error;
    }
  }

  /**
   * Validate transform configuration without saving
   */
  async validateTransformConfiguration(
    transformType: string, 
    values: ActionTransformValues
  ): Promise<ValidationResult> {
    try {
      logger.debug('Validating transform configuration:', { transformType, values });
      const result = await apiClient.post<ValidationResult>(
        `/v1/pipeline/validate-transform/${transformType}`,
        values
      );
      
      logger.debug('Transform configuration validated:', {
        transformType,
        isValid: result.valid,
        errorCount: Object.keys(result.errors || {}).length
      });

      return result;
    } catch (error) {
      logger.error('Failed to validate transform configuration:', error);
      throw error;
    }
  }

  /**
   * Handle component change events for dynamic UI updates
   */
  async handleComponentChange(
    transformType: string, 
    event: ComponentChangeEvent
  ): Promise<ActionTransformUI> {
    try {
      logger.debug('Handling component change:', { transformType, event });
      const updatedUI = await apiClient.post<ActionTransformUI>(
        `/v1/pipeline/component-change/${transformType}`,
        event
      );
      
      logger.debug('Component change handled successfully:', {
        transformType,
        componentId: event.componentId,
        pagesCount: updatedUI.pages?.length || 0
      });

      return updatedUI;
    } catch (error) {
      logger.error('Failed to handle component change:', error);
      throw error;
    }
  }

  /**
   * Get dropdown options for a specific component
   */
  async getDropdownOptions(
    transformType: string, 
    componentId: string, 
    pipelinePath: string,
    transformName: string,
    query?: string
  ): Promise<DropdownOption[]> {
    try {
      logger.debug('Fetching dropdown options:', { transformType, componentId, pipelinePath, transformName, query });
      
      const params: Record<string, string> = { 
        path: pipelinePath,
        transformName: transformName
      };
      if (query) {
        params.query = query;
      }
      
      const options = await apiClient.get<DropdownOption[]>(
        `/v1/pipeline/dropdown-options/${transformType}/${componentId}`,
        { params }
      );
      
      logger.debug('Dropdown options fetched successfully:', {
        transformType,
        componentId,
        pipelinePath,
        transformName,
        optionsCount: options.length
      });

      return options;
    } catch (error) {
      logger.error('Failed to fetch dropdown options:', error);
      throw error;
    }
  }



  /**
   * Create a complete transform configuration object for the frontend
   * This method combines UI configuration with appropriate values (default for new, current for existing)
   */
  async getCompleteTransformConfiguration(
    transformType: string, 
    transformName?: string,
    path?: string
  ): Promise<{
    ui: ActionTransformUI;
    values: ActionTransformValues | null;
  }> {
    try {
      logger.debug('Fetching complete transform configuration:', { 
        transformType, 
        transformName, 
        path
      });

      // Always fetch UI configuration
      const uiPromise = this.getTransformUIConfiguration(transformType);

      // Fetch values based on whether this is a new or existing transform
      let valuesPromise: Promise<ActionTransformValues | null>;
      
      if (transformName && path) {
        // For existing transforms with a known pipeline path, get current values from the pipeline
        logger.debug('📁 Fetching current values from pipeline file');
        valuesPromise = this.getTransformValues(path, transformName, transformType);
      } else if (transformName) {
        // For new transforms OR transforms without pipeline path, use empty values (UI components will provide defaults)
        logger.debug('🆕 No pipeline path, using empty values (UI defaults will be applied)');
        valuesPromise = Promise.resolve({ id: transformName, type: transformType, xLocation: 0, yLocation: 0, values: {} });
      } else {
        // No transform ID available
        logger.debug('❌ No transform name available, skipping values fetch');
        valuesPromise = Promise.resolve(null);
      }

      const [ui, values] = await Promise.all([uiPromise, valuesPromise]);

      logger.debug('Complete transform configuration fetched successfully:', {
        transformType,
        hasValues: !!values,
        hasPath: !!path
      });

      return { ui, values };
    } catch (error) {
      logger.error('Failed to fetch complete transform configuration:', error);
      throw error;
    }
  }

  /**
   * Save transform data when the save button is pressed
   */
  async postTransform(
    transformName: string,
    path: string,
    values: ActionTransformValuesDTO
  ): Promise<ValidationResult> {
    try {
      logger.debug('Saving transform data:', { transformName, path, values });
      logger.debug('Transform payload structure:', {
        id: values.id,
        type: values.type,
        xLocation: values.xLocation,
        yLocation: values.yLocation,
        hasValues: !!values.values,
        valuesKeys: values.values ? Object.keys(values.values) : [],
        fullPayload: JSON.stringify(values, null, 2)
      });
      const result = await apiClient.post<ValidationResult>(
        `/v1/pipeline/postTransform/${transformName}`,
        values,
        { params: { path } }
      );
      
      logger.debug('Transform data saved successfully:', {
        transformName,
        isValid: result.isValid ?? result.valid,
        errors: result.errors
      });

      return result;
    } catch (error: unknown) {
      // Handle 400 Bad Request responses - backend returns ValidationResultDTO in error response
      const apiError = error as { status?: number; details?: ValidationResult };
      if (apiError?.status === 400 && apiError?.details) {
        // The ValidationResultDTO is in the error details
        const validationResult: ValidationResult = {
          isValid: apiError.details.isValid ?? false,
          valid: apiError.details.valid ?? apiError.details.isValid ?? false,
          errors: apiError.details.errors,
          warnings: apiError.details.warnings,
          message: apiError.details.message
        };
        
        logger.debug('Transform validation failed (400 response):', {
          transformName,
          validationResult,
          fullErrorDetails: error.details
        });
        
        return validationResult;
      }
      
      logger.error('Failed to save transform data:', {
        error,
        status: error?.status,
        details: error?.details,
        message: error?.message
      });
      throw error;
    }
  }
}

// Export singleton instance
export const transformsService = new TransformsService(); 